(function () {
  const desktopMedia = window.matchMedia('(min-width: 1041px)');
  const localizedProductCache = new Map();
  const etaConfigCache = { value: null };
  const DEFAULT_CITY_BY_CODE = {
    AE: 'Dubai',
    SA: 'Riyadh',
    QA: 'Doha',
    KW: 'Kuwait City',
    OM: 'Muscat',
    BH: 'Manama',
    US: 'California',
    AU: 'New South Wales',
    CA: 'Ontario'
  };
  let offsetRaf = null;
  let popupRenderId = 0;

  function normalizeImageUrl(url) {
    if (!url || typeof url !== 'string') return '';
    return url.startsWith('//') ? `https:${url}` : url;
  }

  function formatMoney(amount, currency) {
    if (!Number.isFinite(amount)) return '';
    if (typeof window.formatMinorPrice === 'function' && typeof window.formatCurrencySuffix === 'function') {
      const value = window.formatMinorPrice(amount);
      if (currency === 'USD') return `$${value}`;
      const suffix = window.formatCurrencySuffix(currency);
      return suffix ? `${value} ${suffix}` : value;
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${currency}`;
    }
  }

  function getProductHandle(item) {
    if (item.handle) return String(item.handle);
    const match = String(item.url || '').match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function normalizeVariantId(value) {
    const id = String(value || '').trim();
    return /^\d+$/.test(id) ? id : '';
  }

  function normalizeCountryCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function readEtaConfig() {
    if (etaConfigCache.value) return etaConfigCache.value;
    const node = document.getElementById('theme-delivery-eta-config');
    if (!node) {
      etaConfigCache.value = null;
      return etaConfigCache.value;
    }
    try {
      etaConfigCache.value = JSON.parse(node.textContent || '');
    } catch {
      etaConfigCache.value = null;
    }
    return etaConfigCache.value;
  }

  function resolveEtaEndpoint() {
    const config = readEtaConfig();
    const endpoint = String(config && config.endpoint ? config.endpoint : '').trim();
    if (!endpoint) return '';
    if (/^https?:\/\//i.test(endpoint)) return endpoint;
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root.slice(0, -1) : root;
    return endpoint.charAt(0) === '/' ? `${normalizedRoot}${endpoint}` : `${normalizedRoot}/${endpoint}`;
  }

  function readMarketCountryCode() {
    const deliveryCountryData = document.getElementById('delivery-country-data');
    if (deliveryCountryData && deliveryCountryData.textContent) {
      try {
        const parsed = JSON.parse(deliveryCountryData.textContent.trim());
        if (parsed && parsed.iso_code) return normalizeCountryCode(parsed.iso_code);
      } catch {}
    }

    const localizationCountry = document.getElementById('delivery-location-localization-country');
    if (localizationCountry instanceof HTMLInputElement && localizationCountry.value) {
      return normalizeCountryCode(localizationCountry.value);
    }

    const scopedMarket = document.querySelector('[data-delivery-estimate-scope][data-current-market-code]');
    if (scopedMarket instanceof HTMLElement && scopedMarket.dataset.currentMarketCode) {
      return normalizeCountryCode(scopedMarket.dataset.currentMarketCode);
    }

    return normalizeCountryCode(window.theme?.defaultCountry || 'AE');
  }

  function readStoredMarketContext() {
    try {
      const raw = localStorage.getItem('theme_market_context_v1');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function resolveEtaLocation() {
    const stored = readStoredMarketContext();
    const country = normalizeCountryCode(stored?.countryCode || stored?.country || readMarketCountryCode());
    const city = String(stored?.city || DEFAULT_CITY_BY_CODE[country] || '').trim();
    return { country, city };
  }

  function buildEtaUrl(variantId, qty) {
    const endpoint = resolveEtaEndpoint();
    if (!endpoint) return '';
    const normalizedVariantId = normalizeVariantId(variantId);
    if (!normalizedVariantId) return '';
    const location = resolveEtaLocation();
    const params = new URLSearchParams();
    params.set('variant', `${normalizedVariantId}:${Math.max(1, Number(qty) || 1)}`);
    if (location.country) params.set('country', location.country);
    if (location.city) params.set('city', location.city);
    return `${endpoint}?${params.toString()}`;
  }

  function parsePopupEtaEntry(payload, variantId) {
    if (!payload || typeof payload !== 'object') return null;
    const normalizedVariantId = normalizeVariantId(variantId);
    if (!normalizedVariantId) return null;
    const items = payload.items && typeof payload.items === 'object' ? payload.items : null;
    const raw = items && items[normalizedVariantId] ? items[normalizedVariantId] : payload;
    if (!raw || typeof raw !== 'object' || raw.found === false) return null;
    const statusLabel = String(
      raw.label ||
      raw.message ||
      raw.statusLabel ||
      raw.status ||
      raw.dateLabel ||
      ''
    ).trim();
    const minDate = String(raw.minDate || '').trim();
    const maxDate = String(raw.maxDate || minDate).trim();
    return {
      minDate,
      maxDate,
      statusLabel
    };
  }

  function applyPopupStatus(popup, options) {
    const status = popup.querySelector('[data-cart-added-status]');
    const text = popup.querySelector('[data-cart-added-status-text]');
    if (!status || !text) return;
    const readyLabel = popup.dataset.readyLabel || 'Ready to Ship';
    const expectedLabel = popup.dataset.expectedLabel || 'Delivery in 2–3 weeks';

    if (!options || options.mode !== 'delayed' || !options.label) {
      status.classList.remove('cart-added-popup__status--delayed');
      text.textContent = readyLabel;
      return;
    }

    status.classList.add('cart-added-popup__status--delayed');
    text.textContent = options.label || expectedLabel;
  }

  async function syncPopupEtaStatus(item, popup, renderId) {
    const variantId = normalizeVariantId(item?.variant_id || item?.id);
    if (!variantId) {
      applyPopupStatus(popup);
      return;
    }

    const url = buildEtaUrl(variantId, item?.quantity || 1);
    if (!url) {
      applyPopupStatus(popup);
      return;
    }

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`eta_${response.status}`);
      const payload = await response.json();
      if (renderId !== popupRenderId) return;
      const entry = parsePopupEtaEntry(payload, variantId);
      if (!entry) {
        applyPopupStatus(popup);
        return;
      }
      const statusLabel = String(entry.statusLabel || '').trim();
      if (!statusLabel) {
        applyPopupStatus(popup);
        return;
      }
      const readyLabel = String(popup.dataset.readyLabel || 'Ready to Ship').trim().toLowerCase();
      const normalizedLabel = statusLabel.toLowerCase();
      const isReady =
        normalizedLabel === readyLabel ||
        normalizedLabel.indexOf('ready to ship') !== -1 ||
        normalizedLabel.indexOf('جاهز للشحن') !== -1;

      if (isReady) {
        applyPopupStatus(popup);
        return;
      }

      applyPopupStatus(popup, { mode: 'delayed', label: statusLabel });
    } catch {
      if (renderId !== popupRenderId) return;
      applyPopupStatus(popup);
    }
  }

  function getLocalizedProduct(handle) {
    const locale = document.documentElement.lang || 'en';
    const cacheKey = `${locale}:${handle}`;
    if (localizedProductCache.has(cacheKey)) return localizedProductCache.get(cacheKey);

    const root = window.Shopify?.routes?.root || '/';
    const localizedRoot = root.endsWith('/') ? root : `${root}/`;
    const request = fetch(`${localizedRoot}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    localizedProductCache.set(cacheKey, request);
    if (localizedProductCache.size > 32) {
      localizedProductCache.delete(localizedProductCache.keys().next().value);
    }
    return request;
  }

  function updateLocalizedProductCopy(item, popup, renderId) {
    const locale = document.documentElement.lang || 'en';
    const handle = getProductHandle(item);
    if (!handle || /^en(?:-|$)/i.test(locale)) return;

    getLocalizedProduct(handle).then((product) => {
      if (!product || renderId !== popupRenderId || !popup.classList.contains('am-popup--active')) return;

      const title = popup.querySelector('[data-cart-added-title]');
      const variant = popup.querySelector('[data-cart-added-variant]');
      const image = popup.querySelector('[data-cart-added-image]');
      const translatedTitle = product.title || '';
      const translatedVariant = product.variants?.find((candidate) => String(candidate.id) === String(item.variant_id || item.id));
      const translatedVariantTitle = translatedVariant?.title && translatedVariant.title !== 'Default Title'
        ? translatedVariant.title
        : '';

      if (title && translatedTitle) title.textContent = translatedTitle;
      if (image instanceof HTMLImageElement && translatedTitle) image.alt = translatedTitle;
      if (variant && translatedVariantTitle) {
        variant.textContent = translatedVariantTitle;
        variant.removeAttribute('hidden');
      }
    });
  }

  function syncDesktopOffset() {
    const popup = document.getElementById('am-popup-cart-added');
    if (!popup || !desktopMedia.matches) return;

    const header = document.getElementById('header-group');
    const headerBottom = header?.getBoundingClientRect().bottom || 0;
    const fallbackHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 0;
    popup.style.setProperty('--cart-added-popup-desktop-top', `${Math.max(8, Math.round(headerBottom || fallbackHeight) + 8)}px`);
  }

  function queueDesktopOffsetSync() {
    if (offsetRaf !== null) return;
    offsetRaf = window.requestAnimationFrame(() => {
      offsetRaf = null;
      syncDesktopOffset();
    });
  }

  function openCartAddedPopup(popup) {
    const dialog = popup.querySelector('[role="dialog"]');

    if (!desktopMedia.matches) {
      dialog?.setAttribute('aria-modal', 'true');
      window.openPopup?.('cart-added');
      return;
    }

    syncDesktopOffset();
    dialog?.setAttribute('aria-modal', 'false');
    popup.style.display = 'flex';
    popup.classList.add('am-popup--active', 'cart-added-popup--desktop');
    popup.querySelector('.am-popup__close-btn')?.focus({ preventScroll: true });
  }

  window.showProductAddNotification = function (item, contextElement, cartData) {
    const showPopup = (cart) => {
      const variantInput = contextElement instanceof Element
        ? contextElement.querySelector('input[name="id"]')
        : null;
      const variantId = variantInput?.value;
      const cartItem = item || cart?.items?.find((entry) => String(entry.variant_id) === String(variantId));

      if (cartItem) {
        window.showCartAddedPopup(cartItem, cart);
      }
    };

    if (cartData?.items) {
      showPopup(cartData);
      return;
    }

    const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;
    fetch(cartUrl)
      .then((response) => (response.ok ? response.json() : null))
      .then(showPopup)
      .catch(() => {});
  };

  window.showCartAddedPopup = function (item, cart) {
    const popup = document.getElementById('am-popup-cart-added');
    if (!popup || !item) return;

    const image = popup.querySelector('[data-cart-added-image]');
    const title = popup.querySelector('[data-cart-added-title]');
    const variant = popup.querySelector('[data-cart-added-variant]');
    const price = popup.querySelector('[data-cart-added-price]');
    const viewCart = popup.querySelector('[data-cart-added-view-cart]');
    const imageUrl = normalizeImageUrl(item.image || item.featured_image?.url || '');
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const currency = cart?.currency || popup.dataset.currency || 'AED';
    const renderId = ++popupRenderId;

    if (image instanceof HTMLImageElement) {
      image.src = imageUrl;
      image.alt = item.product_title || item.title || '';
      image.closest('.cart-added-popup__media')?.toggleAttribute('hidden', !imageUrl);
    }
    if (title) title.textContent = item.product_title || item.title || '';
    if (variant) {
      variant.textContent = variantTitle;
      variant.toggleAttribute('hidden', !variantTitle);
    }
    if (price) price.textContent = formatMoney(item.final_price ?? item.price, currency);
    if (viewCart) {
      viewCart.textContent = (popup.dataset.viewCartLabel || 'View cart (__COUNT__)')
        .replace('__COUNT__', String(cart?.item_count || 0));
    }
    applyPopupStatus(popup);

    window.hideSystemToast?.();
    openCartAddedPopup(popup);
    updateLocalizedProductCopy(item, popup, renderId);
    syncPopupEtaStatus(item, popup, renderId);
  };

  window.addEventListener('scroll', queueDesktopOffsetSync, { passive: true });
  window.addEventListener('resize', queueDesktopOffsetSync, { passive: true });
  desktopMedia.addEventListener?.('change', () => {
    const popup = document.getElementById('am-popup-cart-added');
    if (!popup?.classList.contains('am-popup--active')) return;
    window.closePopup?.('cart-added');
    popup.classList.remove('cart-added-popup--desktop');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !desktopMedia.matches) return;
    const popup = document.getElementById('am-popup-cart-added');
    if (!popup?.classList.contains('cart-added-popup--desktop')) return;
    window.closePopup?.('cart-added');
    popup.classList.remove('cart-added-popup--desktop');
  });
})();
